import { ManualOverrideInfo } from "verdi-common/schemas/commonSubSchemas/manualOverrideInfo";
import type DeviceMapEntity from "../../../map-management/map-entities/device-map-entity";
import type MapEntityBase from "../../../map-management/map-entities/map-entity-base";

import { doesDeviceVersionMatch, GENERATION_CODE } from "../../versions-code";
import { OPERATING_MODE } from "./definitions";

/**
 * Get the operating mode info for a device
 * @param model - The model of the device
 * @returns The operating mode info for the device
 */
export const getOperatingModeInfo = ({ model }: { model: DeviceMapEntity["model"] }): ManualOverrideInfo => {
    const isSproutDevice = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.SPROUT });
    const isGen4Device = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.GEN4 });

    if (isGen4Device && model?.gen4NetworkInfo?.manualOverrideInfo) {
        return model.gen4NetworkInfo.manualOverrideInfo as ManualOverrideInfo;
    }

    if (isSproutDevice && model?.sproutNetworkInfo?.manualOverrideInfo) {
        return model.sproutNetworkInfo.manualOverrideInfo as ManualOverrideInfo;
    }

    return new ManualOverrideInfo({
        deviceOverrideSetting: undefined,
        deviceOverrideSettingLastUpdated: new Date(),
        remoteOverrideSetting: undefined,
        remoteOverrideSettingLastUpdated: new Date(),
    });
};

export const getOperatingMode = ({ model }: { model: DeviceMapEntity["model"] }) => {
    const manualOverrideInfo = getOperatingModeInfo({ model });

    // If the device has never been set to manual mode
    if (
        manualOverrideInfo.deviceOverrideSetting === undefined &&
        manualOverrideInfo.remoteOverrideSetting === undefined
    ) {
        return {
            operatingMode: null,
            targetOperatingMode: null,
            isManualModeTransitioning: false,
        };
    }

    // If the device updated more recently than the remote, use the device's mode, otherwise the target is the remote mode
    const targetOperatingMode = ManualOverrideInfo.getters.getIdealManualOverrideState(manualOverrideInfo);

    const deviceOperatingMode = manualOverrideInfo?.deviceOverrideSetting;

    return {
        // Cast to the OPERATING_MODE enum
        operatingMode: deviceOperatingMode as OPERATING_MODE,
        targetOperatingMode: targetOperatingMode as OPERATING_MODE,
        // If the deviceOperatingMode is undefined, but the remoteOperatingMode is defined, the device is transitioning
        // OR if the two operating modes are different, the device is transitioning
        isManualModeTransitioning: deviceOperatingMode === undefined || targetOperatingMode !== deviceOperatingMode,
    };
};

/**
 * Device is in manual mode (not necessarily manual mode irrigating) if manualOverrideEngaged is true
 */
export function isDeviceInManualMode({ model }: { model: MapEntityBase["model"] }) {
    const isSproutDevice = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.SPROUT });
    const isGen4Device = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.GEN4 });

    if (isGen4Device) {
        return (
            model.gen4NetworkInfo?.manualOverrideInfo?.deviceOverrideSetting &&
            model.gen4NetworkInfo?.manualOverrideInfo?.deviceOverrideSetting !== OPERATING_MODE.SCHEDULE
        );
    }
    if (isSproutDevice) {
        return (
            model.sproutNetworkInfo?.manualOverrideInfo?.deviceOverrideSetting &&
            model.sproutNetworkInfo?.manualOverrideInfo?.deviceOverrideSetting !== OPERATING_MODE.SCHEDULE
        );
    }
    return false;
}
